import { useState, useMemo, useCallback } from 'react';
import {
  FilterOperator,
  FilterValue,
  FilterParams,
} from '@antimatterhq/ui/types/TableFilter';

export function useTableColumnFiltering<T>(items: T[]) {
  const [filters, setFilters] = useState<FilterParams>({});

  // Filter the items based on all applied filters
  const filteredItems = useMemo(() => {
    const filterIds = Object.keys(filters);
    if (filterIds.length === 0) return items; // No filters applied

    // Filter the items based on all applied filters
    return items.filter((item) => {
      return filterIds.every((filterId) => {
        const columnId = filterId as keyof T;
        const itemValue = item[columnId];
        const { value: filterValue, operator = 'contains' } = filters[filterId];

        if (filterValue === null || filterValue === '') return true;

        if (itemValue === undefined || itemValue === null) return false;

        const itemValueStr = String(itemValue).toLowerCase();
        const filterValueStr = String(filterValue).toLowerCase();

        // Apply the filter based on the operator
        switch (operator) {
          case 'equals':
            return itemValueStr === filterValueStr;
          case 'contains':
            return itemValueStr.includes(filterValueStr);
          case 'startsWith':
            return itemValueStr.startsWith(filterValueStr);
          case 'endsWith':
            return itemValueStr.endsWith(filterValueStr);
          case 'gt':
            return Number(itemValue) > Number(filterValue);
          case 'gte':
            return Number(itemValue) >= Number(filterValue);
          case 'lt':
            return Number(itemValue) < Number(filterValue);
          case 'lte':
            return Number(itemValue) <= Number(filterValue);
          default:
            return itemValueStr.includes(filterValueStr);
        }
      });
    });
  }, [items, filters]);

  // Add or update a filter
  const setFilter = useCallback(
    (id: string, value: FilterValue, operator: FilterOperator = 'contains') => {
      setFilters((prev) => {
        // If value is empty or null, remove the filter
        if (value === null || value === '') {
          const { [id]: removed, ...rest } = prev;
          return rest;
        }

        // Add or update filter
        return {
          ...prev,
          [id]: { value, operator },
        };
      });
    },
    [],
  );

  // Remove a specific filter
  const removeFilter = useCallback((id: string) => {
    setFilters((prev) => {
      const { [id]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Convert filters object to array for backward compatibility if needed
  const filtersArray = useMemo(() => {
    return Object.entries(filters).map(([id, { value, operator }]) => ({
      id,
      value,
      operator,
    }));
  }, [filters]);

  return {
    filteredItems,
    filters,
    filtersArray, // Added for backward compatibility
    setFilter,
    removeFilter,
    clearFilters,
    hasFilters: Object.keys(filters).length > 0,
  };
}
